import { Anchor, Button, Tooltip, useComputedColorScheme } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconChevronLeft, IconCopy, IconPlayerPlay } from '@tabler/icons-react';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useAwareZindex } from '~/hooks/context/useZIndex';
import { useDuplicateTest } from '~/hooks/test/useDuplicateTest';
import { useResetRecorderAtoms } from '~/hooks/vm/useResetRecorder';
import { api } from '~/trpc-extra/api';
import EditableTitle from '../common/EditableTitle';
import CodeViewSwitcher from '../testDraft/CodeViewSwitcher';
import { EditTestButton } from './EditTestButton';
import { openSingleTestRunModal } from './SingleTestRunModal';
import { VersionSwitcher } from './version/VersionSwitcher';

const BreadcrumbNav = ({ projectId }: { projectId?: string }) => {
  return (
    <>
      <Anchor
        component={Link}
        href={`/project/${projectId}/test`}
        className="text-zinc-700 dark:text-zinc-400"
      >
        <IconChevronLeft size={16} />
      </Anchor>
      <Anchor
        href={`/project/${projectId}/test`}
        key="tests"
        component={Link}
        className="text-sm text-zinc-700 dark:text-zinc-400"
      >
        Tests
      </Anchor>
      <div className="text-zinc-700 dark:text-zinc-400">/</div>
    </>
  );
};

export default function TestHeader({
  testId,
  testContentId,
  selectedTestContentVersion,
  setSelectedTestContentVersion,
  isInDrawer,
}: {
  testId: string;
  testContentId: string;
  selectedTestContentVersion: string;
  setSelectedTestContentVersion: (version: string) => void;
  isInDrawer: boolean;
}) {
  const projectId = useProjectId();
  const trpcUtils = api.useUtils();
  const contextZIndex = useAwareZindex();

  const resetRecorderAtoms = useResetRecorderAtoms(testContentId);

  const { data: testMeta } = api.test.getMeta.useQuery({ id: testId }, { enabled: !!testId });

  const [editingTitle, setEditingTitle] = useState(false);

  const colorScheme = useComputedColorScheme();

  const updateTest = api.test.updateTest.useMutation({
    onSuccess: () => {
      trpcUtils.test.getTest.invalidate().catch(() => undefined);
      trpcUtils.testContent.get.invalidate().catch(() => undefined);
      trpcUtils.test.listTests.invalidate().catch(() => undefined);
      setEditingTitle(false);
    },
    onError: (e) => {
      console.error(e);
      notifications.show({ message: 'Error saving', color: 'red' });
    },
  });

  const { isDuplicating, handleDuplicateClick } = useDuplicateTest({
    testId,
    projectId,
    testMeta,
    zIndex: contextZIndex + 100,
  });

  // empty dependency array because we want to do this exactly once on page cleanup
  useEffect(() => {
    return () => {
      // call the reset function on cleanup. Using "void" ensures any promise is ignored.
      void resetRecorderAtoms();
    };
  }, []);

  return (
    <div
      className={`flex items-center justify-between bg-white px-6 pb-4 pt-2 ${editingTitle ? '' : 'py-3'} font-medium dark:bg-[#242424]`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2 pr-2">
        {!isInDrawer && <BreadcrumbNav projectId={projectId} />}
        <EditableTitle
          title={testMeta?.name || 'Untitled Test'}
          tooltip="Click to edit the test name"
          onClick={() => setEditingTitle(true)}
          handleSave={async (newName) => {
            await updateTest.mutateAsync({ testId, testData: { name: newName } });
          }}
        />
      </div>

      <div className="flex items-center space-x-3">
        <Button
          variant="default"
          leftSection={<IconCopy size={14} />}
          loading={isDuplicating}
          onClick={handleDuplicateClick}
        >
          Duplicate Test
        </Button>
        <VersionSwitcher
          testId={testId}
          selectedTestContentVersion={selectedTestContentVersion}
          setSelectedTestContentVersion={setSelectedTestContentVersion}
        />
        <CodeViewSwitcher color={colorScheme === 'light' ? 'light' : 'dark'} />
        {!selectedTestContentVersion && <EditTestButton testContentId={testContentId} />}
        {!selectedTestContentVersion && (
          <Tooltip
            label="Publish the test before running"
            disabled={!!(projectId && testMeta?.trunkContentId)}
          >
            <div>
              <Button
                variant="filled"
                leftSection={<IconPlayerPlay size={14} />}
                onClick={() => {
                  if (!projectId || !testMeta?.trunkContentId) {
                    return;
                  }
                  openSingleTestRunModal({
                    projectId,
                    testId,
                    testName: testMeta.name || 'Single Test Run',
                    zIndex: contextZIndex + 100,
                  });
                }}
                disabled={!projectId || !testMeta?.trunkContentId}
              >
                Run Test
              </Button>
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
